import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAdminCompanyDetail } from "@/lib/api/companies.queries";
import { useAdminDianProcesses } from "@/lib/api/dian-processes.queries";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TH, TBody, TR, TD } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { CertStatusBadge, DianStatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { CertRequestPanel } from "./CertRequestPanel";
import { InventoryPanel } from "./InventoryPanel";

type Tab =
  | "overview"
  | "certificado"
  | "cert-request"
  | "procesos"
  | "inventario";

export function CompanyDetail() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  const query = useAdminCompanyDetail(companyId);
  const processesQuery = useAdminDianProcesses({ companyId, pageSize: 20, page: 1 });

  if (query.isPending) return <CenteredSpinner label="Cargando empresa…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const c = query.data;

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "certificado", label: "Certificado" },
    { key: "cert-request", label: "Solicitud cert" },
    { key: "procesos", label: "Procesos DIAN" },
    { key: "inventario", label: "Inventario" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/companies")} className="mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Volver a empresas
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">{c.legalName}</h1>
              <CertStatusBadge status={c.certStatus ?? "missing"} />
            </div>
            <p className="text-sm text-slate-500 mt-0.5 font-mono">
              NIT {c.nit}{c.dv ? `-${c.dv}` : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-0.5">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === key
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle>Información general</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p><span className="text-slate-500">NIT-DV:</span> <span className="font-mono">{c.nit}-{c.dv ?? "?"}</span></p>
              <p><span className="text-slate-500">Razón social:</span> {c.legalName}</p>
              {c.tradeName && <p><span className="text-slate-500">Nombre comercial:</span> {c.tradeName}</p>}
              <p><span className="text-slate-500">Régimen:</span> {c.regimeCode ?? "—"}</p>
              <p><span className="text-slate-500">Onboarding:</span> <span className="capitalize">{c.onboardingStatus}</span></p>
              <p><span className="text-slate-500">Ambiente DIAN:</span>{" "}
                <span className={cn(
                  "text-xs font-medium px-1.5 py-0.5 rounded",
                  c.dianEnvironment === "prod" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-700"
                )}>
                  {c.dianEnvironment}
                </span>
              </p>
              <p><span className="text-slate-500">Equipo:</span> <span className="font-mono text-xs">{c.teamId}</span></p>
              <p><span className="text-slate-500">Creada:</span> {formatDate(c.createdAt)}</p>
            </CardContent>
          </Card>

          {c.contactJson != null && (
            <Card>
              <CardHeader><CardTitle>Contacto</CardTitle></CardHeader>
              <CardContent>
                <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap break-all">
                  {JSON.stringify(c.contactJson as Record<string, unknown>, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === "certificado" && (
        <Card>
          <CardHeader><CardTitle>Certificado DIAN (solo lectura)</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><span className="text-slate-500">Estado:</span> <CertStatusBadge status={c.certStatus ?? "missing"} /></p>
            {c.certUploadedAt && (
              <p><span className="text-slate-500">Cargado:</span> {formatDate(c.certUploadedAt)}</p>
            )}
            <p className="text-xs text-slate-400 mt-3">
              El certificado se conserva cifrado en el almacén seguro de PahFacturar. Aquí solo se muestra su metadata.
            </p>
          </CardContent>
        </Card>
      )}

      {tab === "cert-request" && companyId && (
        <CertRequestPanel companyId={companyId} />
      )}

      {tab === "procesos" && (
        <Card className="overflow-hidden">
          <CardHeader><CardTitle>Procesos DIAN recientes</CardTitle></CardHeader>
          {processesQuery.isPending ? (
            <CenteredSpinner label="Cargando procesos…" />
          ) : processesQuery.isError ? (
            <ErrorState error={processesQuery.error} />
          ) : (processesQuery.data?.processes ?? []).length === 0 ? (
            <CardContent><p className="text-sm text-slate-500">No hay procesos para esta empresa.</p></CardContent>
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Process ID</TH>
                  <TH>Tipo</TH>
                  <TH>Documento</TH>
                  <TH>Estado</TH>
                  <TH>Creado</TH>
                </tr>
              </THead>
              <TBody>
                {processesQuery.data!.processes.map((p) => (
                  <TR key={p.processId}>
                    <TD className="font-mono text-[11px]">{p.processId}</TD>
                    <TD className="text-xs">{p.documentType}</TD>
                    <TD className="font-mono text-[11px]">{p.sourceDocumentId ?? "—"}</TD>
                    <TD><DianStatusBadge status={p.status} /></TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(p.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      )}

      {tab === "inventario" && companyId && (
        <InventoryPanel companyId={companyId} />
      )}

    </div>
  );
}
